import React from 'react';
import { HStack, Button, Text, Select } from '@chakra-ui/react';

/**
 * Reusable pagination component.
 *
 * @param {number} currentPage - Current active page (1-based)
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Callback when page changes
 * @param {number} totalItems - Total number of items (for display)
 * @param {number} itemsPerPage - Items per page (for display)
 */
export function Pagination({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= maxVisible; i++) pages.push(i);
    } else if (currentPage >= totalPages - 2) {
      for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);
    }

    return pages;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <HStack spacing={2} justify="center" align="center" py={4} flexWrap="wrap">
      {totalItems > 0 && (
        <Text fontSize="sm" color="gray.500" mr={4}>
          Showing {startItem}–{endItem} of {totalItems}
        </Text>
      )}
      <Button
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        isDisabled={currentPage === 1}
        variant="outline"
      >
        Previous
      </Button>
      {getPageNumbers().map((pageNum) => (
        <Button
          key={pageNum}
          size="sm"
          colorScheme={currentPage === pageNum ? 'blue' : 'gray'}
          variant={currentPage === pageNum ? 'solid' : 'outline'}
          onClick={() => onPageChange(pageNum)}
        >
          {pageNum}
        </Button>
      ))}
      <Button
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        isDisabled={currentPage === totalPages}
        variant="outline"
      >
        Next
      </Button>
    </HStack>
  );
}

/**
 * Hook to manage pagination state.
 * Returns [paginatedData, paginationProps]
 */
export function usePagination(rawData, itemsPerPage = 25) {
  const data = rawData || [];
  const [currentPage, setCurrentPage] = React.useState(1);

  // Reset to page 1 when data changes length significantly (e.g., filter applied)
  const prevLength = React.useRef(data.length);
  React.useEffect(() => {
    if (Math.abs(data.length - prevLength.current) > itemsPerPage) {
      setCurrentPage(1);
    }
    prevLength.current = data.length;
  }, [data.length, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));

  // Clamp page synchronously to avoid empty flash
  const safePage = currentPage > totalPages ? totalPages : currentPage;

  const startIndex = (safePage - 1) * itemsPerPage;
  const paginatedData = data.slice(startIndex, startIndex + itemsPerPage);

  // Sync state if clamped
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginationProps = {
    currentPage: safePage,
    totalPages,
    onPageChange: setCurrentPage,
    totalItems: data.length,
    itemsPerPage,
  };

  return [paginatedData, paginationProps, setCurrentPage];
}

export default Pagination;
